import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DepartmentsResponse, TeamRosterResponse, TeamsResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import styles from './RolesPage.module.css';

/**
 * Departments — the organisation structure browsing page (Mission 162's
 * Team/Department Roster increment).
 *
 * ---------------------------------------------------------------------
 * WHY THIS PAGE EXISTS NOW, AFTER "TEAMS ROSTER SCOPE" DECIDED AGAINST ONE
 *
 * `docs/DOMAIN_DECISIONS.md` "Teams Roster Scope" (2026-09-16, TR-1)
 * decided the Dashboard's own roster widget satisfied MVP and that no
 * dedicated `/teams` destination was required. Mission 162 revisits that:
 * the Phase 2 reconnaissance that preceded it found the widget-only
 * approach left no way to go *from* a department or team *to* who is
 * currently on it — a real gap, not a preference — and this mission's own
 * brief explicitly names "the missing browsing/surfacing experience" as
 * work to do. This page is that experience, superseding TR-1 rather than
 * contradicting it by oversight; see `docs/DOMAIN_DECISIONS.md`'s Mission
 * 162 entry for the recorded decision.
 *
 * WHAT THIS PAGE DOES NOT DO
 *
 * It does not create, edit or retire structure (that remains
 * `/organisation`'s Departments/Teams sections, admin-only) and it does not
 * change anyone's placement (that remains Employee Placement, admin-only).
 * It reads the same structure those already manage. A roster lists
 * identity only — no role, capability, rating or ranking — the same
 * restraint `RolesPage.tsx` already documents for its own library view.
 * ---------------------------------------------------------------------
 */
export function DepartmentsPage() {
  const departments = useApiResource<DepartmentsResponse>('/departments');
  const teams = useApiResource<TeamsResponse>('/teams');
  const [openTeamId, setOpenTeamId] = useState<string | null>(null);

  if (departments.status === 'loading' || teams.status === 'loading') {
    return <LoadingState label="Loading departments…" />;
  }
  if (departments.status === 'failed') {
    return <ErrorState title="Departments could not be loaded" detail={describeError(departments.error)} />;
  }
  if (teams.status === 'failed') {
    return <ErrorState title="Teams could not be loaded" detail={describeError(teams.error)} />;
  }

  const teamsByDepartment = new Map<string, TeamsResponse['teams']>();
  for (const team of teams.data.teams) {
    const bucket = teamsByDepartment.get(team.departmentId);
    if (bucket) bucket.push(team);
    else teamsByDepartment.set(team.departmentId, [team]);
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Departments</h1>
        <p className={styles.scope}>
          {departments.data.count} {departments.data.count === 1 ? 'department' : 'departments'}
        </p>
      </header>

      <p className={styles.note}>
        The organisation's structure, and who currently sits where. This is a read-only view — structure itself is
        defined in Organisation admin, and placement is changed there too.
      </p>

      {departments.data.departments.length === 0 ? (
        <EmptyState title="No departments are defined" detail="The organisation structure is empty, so there is nothing to browse yet." />
      ) : (
        departments.data.departments.map((department) => {
          const departmentTeams = teamsByDepartment.get(department.id) ?? [];
          return (
            <section key={department.id} className={styles.group} aria-label={department.name}>
              <h2 className={styles.groupHeading}>{department.name}</h2>
              {departmentTeams.length === 0 ? (
                <p className={styles.note}>No active teams recorded in this department.</p>
              ) : (
                <ul className={styles.list}>
                  {departmentTeams.map((team) => (
                    <li key={team.id}>
                      <button
                        type="button"
                        className={styles.card}
                        onClick={() => setOpenTeamId((current) => (current === team.id ? null : team.id))}
                        aria-expanded={openTeamId === team.id}
                      >
                        <span className={styles.name}>{team.name}</span>
                        <span className={styles.meta}>
                          {team.currentMemberCount} {team.currentMemberCount === 1 ? 'member' : 'members'}
                        </span>
                      </button>
                      {openTeamId === team.id ? <TeamRoster teamId={team.id} /> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}

function TeamRoster({ teamId }: { teamId: string }) {
  const roster = useApiResource<TeamRosterResponse>(`/teams/${teamId}/roster`);

  if (roster.status === 'loading') return <LoadingState label="Loading roster…" />;
  if (roster.status === 'failed') {
    return <ErrorState title="This team's roster could not be loaded" detail={describeError(roster.error)} />;
  }

  if (roster.data.members.length === 0) {
    return <p className={styles.note}>No employees are currently assigned to this team.</p>;
  }

  return (
    <ul className={styles.list}>
      {roster.data.members.map((member) => (
        <li key={member.id}>
          <Link to={`/people/${member.id}`} className={styles.card}>
            <span className={styles.name}>{member.fullName}</span>
            {member.id === roster.data.team.currentLeadEmployeeId ? <span className={styles.meta}>Team lead</span> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}
